import React from 'react';
import { ItemsDto } from '../../../../../types/items';
import { AdvancedItemConfigDto } from '../../../../../types/advancedItemConfig';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { isTrackedBatch } from '../../../../../utils/itemType';
import { formatDateTime, formatQty } from '../format';
import { codeValue, DataPanel, DataRow, YesNo } from './DataPanel';

interface ItemStockControlSectionProps {
  item: ItemsDto;
  config: AdvancedItemConfigDto | null;
}

/**
 * Begge roller: lagerstyring, plassering (inkl. reolstørrelser) og konfigurasjon som paneler.
 * Fritekst og endringslogg er egne eksporter under. Kun visning — redigering i Vedlikehold lager.
 */
export const ItemStockControlSection: React.FC<ItemStockControlSectionProps> = ({ item, config }) => {
  const { t } = useTranslation();
  const k = (key: string, fallback: string, params: Record<string, string> = {}) =>
    t(`warehouse.modules.itemCard.stockControl.${key}`, params, fallback);
  const yes = t('warehouse.modules.itemAdmin.fields.yes', {}, 'Ja');
  const no = t('warehouse.modules.itemAdmin.fields.no', {}, 'Nei');

  const binSizes = config?.itemBinSizes ?? [];

  return (
    <>
      <DataPanel title={k('stockControl', 'Lagerstyring')}>
        <DataRow label={k('binGroup', 'Lokasjonsgruppe')} value={codeValue(config?.binGrpNo, config?.binGrpDescription)} />
        <DataRow label={k('pickRule', 'Plukkregel')} value={codeValue(config?.pickRuleNo, config?.pickRuleName)} />
        <DataRow label={k('movementType', 'Bevegelsestype')} value={codeValue(config?.movementTypeNo, config?.movementTypeDescription)} />
        <DataRow label={k('boxType', 'Sorteringsboks')} value={codeValue(config?.stcBoxTypeID, config?.stcBoxTypeDescription)} />
        <DataRow label={k('report', 'Etikettrapport')} value={codeValue(item.reportID, config?.reportName)} />
      </DataPanel>

      <DataPanel title={k('placement', 'Plassering')}>
        {/* Samme navn som i Vedlikehold lager: StdLocationName = «Standard lokasjon» (fra ERP),
            StdBinCode = «Fast plukklokasjon» (WMS, overskrives ikke av ERP-import). Lagersted er WarehouseID. */}
        <DataRow label={k('stdLocation', 'Standard lokasjon')} value={item.stdLocationName} mono />
        <DataRow label={k('fixedPickBin', 'Fast plukklokasjon')} value={config?.stdBinCode || item.stdBinCode} mono />
        <DataRow label={k('dedicatedBin', 'Dedikert lokasjon')} value={config?.dedicatedBinCode} mono />
        <DataRow label={k('binSizeGroup', 'Reolstørrelsesgruppe')} value={codeValue(item.itemBinSizeGroupID, config?.itemBinSizeGroupDescription)} />
        {binSizes.length > 0 && (
          <div className="px-4 py-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="py-1 text-left font-medium">{k('binSize', 'Reolstørrelse')}</th>
                  <th className="py-1 text-right font-medium">{k('maxQty', 'Maks antall')}</th>
                  <th className="py-1 text-right font-medium">{k('putAwayPriority', 'Utleggsprioritet')}</th>
                </tr>
              </thead>
              <tbody>
                {binSizes.map(bs => (
                  <tr key={bs.id} className="text-gray-900">
                    <td className="py-1">{bs.binSizeNo}</td>
                    <td className="py-1 text-right font-medium">{formatQty(bs.maxQuantity)}</td>
                    <td className="py-1 text-right">{bs.putAwayPriorityNo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DataPanel>

      <DataPanel title={k('configuration', 'Konfigurasjon')}>
        <DataRow label={k('stockTransactions', 'Lagerføres')} value={<YesNo value={item.stcTransactionsYesNo} yes={yes} no={no} />} />
        <DataRow label={k('sharedBin', 'Felles lokasjon')} value={<YesNo value={item.useSharedBinYesNo} yes={yes} no={no} />} />
        <DataRow label={k('newLine', 'Tilleggsvare')} value={<YesNo value={item.newLineYesNo} yes={yes} no={no} />} />
        {/* Unntaksflagg vareskann (TW 36344429): NULL = arv fra profil */}
        <DataRow
          label={k('scanItem', 'Krev vareskann')}
          value={item.barCodeConfirmItem == null
            ? k('scanInherit', 'Arv (fra profil)')
            : <YesNo value={item.barCodeConfirmItem} yes={yes} no={no} />}
        />
        <DataRow label={k('putAwayToPickable', 'Utlegg til plukkbar lokasjon')} value={<YesNo value={item.putAwayToPickableBinYesNo} yes={yes} no={no} />} />
        {/* Ikke aktuelt (ikke tomt) for andre enn lagerstyrt batch — derfor skjult */}
        {isTrackedBatch(item.itemType) && (
          <DataRow label={k('mixedBatches', 'Tillat blandede batcher')} value={<YesNo value={item.allowMixedBatches} yes={yes} no={no} />} />
        )}
        <DataRow
          label={k('maxReplenish', 'Maks påfylling')}
          value={item.useMaxReplenishQtyYesNo ? formatQty(item.maxReplenishQty) : <YesNo value={false} yes={yes} no={no} />}
        />
      </DataPanel>

    </>
  );
};

/**
 * Fritekst 1–10, App-fritekst 1–2 (fra ERP) og Intern fritekst 1–4 (WMS-eid, overskrives ikke av import).
 * Lagersjef. Alle vises alltid — tomt felt er også informasjon.
 */
export const ItemFreeTextPanel: React.FC<{ item: ItemsDto }> = ({ item }) => {
  const { t } = useTranslation();
  const k = (key: string, fallback: string, params: Record<string, string> = {}) =>
    t(`warehouse.modules.itemCard.stockControl.${key}`, params, fallback);
  const label = k('freeText', 'Fritekst');
  const freeTexts = [item.freeTxt1, item.freeTxt2, item.freeTxt3, item.freeTxt4, item.freeTxt5,
    item.freeTxt6, item.freeTxt7, item.freeTxt8, item.freeTxt9, item.freeTxt10];
  const appFreeTexts = [item.appFreeField1, item.appFreeField2];
  const internalFreeTexts = [item.internalFree1, item.internalFree2, item.internalFree3, item.internalFree4];

  return (
    <DataPanel title={k('freeTexts', 'Fritekst')}>
      {freeTexts.map((value, i) => <DataRow key={`f${i}`} label={`${label} ${i + 1}`} value={value} />)}
      {appFreeTexts.map((value, i) => (
        <DataRow key={`a${i}`} label={k('appFreeText', `App-fritekst ${i + 1}`, { '0': String(i + 1) })} value={value} />
      ))}
      {internalFreeTexts.map((value, i) => (
        <DataRow key={`i${i}`} label={k('internalFreeText', `Intern fritekst ${i + 1}`, { '0': String(i + 1) })} value={value} />
      ))}
    </DataPanel>
  );
};

/** Endringslogg som én linje nederst i Varedata (lagersjef). */
export const ItemAuditLine: React.FC<{ item: ItemsDto }> = ({ item }) => {
  const { t } = useTranslation();
  const created = formatDateTime(item.created as unknown as string);
  const updated = formatDateTime(item.lastUpdate as unknown as string);
  const by = (who?: string | null) => who || '–';

  return (
    <p className="text-xs text-gray-500 px-1">
      {t('warehouse.modules.itemCard.stockControl.createdLine', { '0': created, '1': by(item.createdBy) }, `Opprettet ${created} av ${by(item.createdBy)}`)}
      <span className="mx-2">·</span>
      {t('warehouse.modules.itemCard.stockControl.updatedLine', { '0': updated, '1': by(item.updatedBy) }, `Sist endret ${updated} av ${by(item.updatedBy)}`)}
    </p>
  );
};
